import { phaseOrderRange, type CampaignEmail, type CampaignPhase, type EmailCampaign } from "./email-campaign";

// The campaign drawn as a send timeline: phases as bands on a spine, every
// e-mail a card that states its order, its send day and its segment before
// the client opens anything. The card carries its own copy button, so the
// client can hand an e-mail to their mail tool without reading it first.
//
// An OPEN card is the head of the opened e-mail: it holds both copy buttons
// and it sticks to the top while the body scrolls past, so the subject and
// the way to copy it are never out of reach. The opened e-mail therefore has
// exactly ONE head - the first build printed the order, the subject and a
// second identical "Zkopírovat text" twice within 60 px.

function replace(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    template
  );
}

function copyLabel(state: "idle" | "done" | "failed", label: string, t: (key: string) => string): string {
  if (state === "done") return t("sales.emails.copied");
  if (state === "failed") return t("sales.emails.copyFailedShort");
  return label;
}

export function EmailCard(props: {
  email: CampaignEmail;
  total: number;
  open: boolean;
  copyState: "idle" | "done" | "failed";
  subjectCopyState: "idle" | "done" | "failed";
  t: (key: string) => string;
  onOpen: () => void;
  onCopy: () => void;
  onCopySubject: () => void;
}) {
  const { email } = props;
  return (
    <article className="ec-card" data-open={props.open ? "true" : undefined} data-email-order={email.order}>
      <div className="ec-card-rail" aria-hidden="true"><span className="ec-card-dot" /></div>
      <div className="ec-card-body">
        <p className="ec-card-meta">
          <span className="ec-order">{email.order}</span>
          {email.day && <span className="ec-chip ec-chip-day">{email.day}</span>}
          {email.segment && <span className="ec-chip ec-chip-segment">{email.segment}</span>}
        </p>
        <h4 className="ec-card-subject">
          <button type="button" className="ec-card-open" onClick={props.onOpen} aria-expanded={props.open}>
            {email.subject || replace(props.t("sales.emails.card.untitled"), { order: email.order })}
          </button>
        </h4>
        {email.task && !props.open && <p className="ec-card-task">{email.task}</p>}
        <div className="ec-card-actions">
          <button
            type="button"
            className={props.open ? "primary-button compact-button" : "secondary-button compact-button"}
            onClick={props.onCopy}
          >
            {copyLabel(props.copyState, props.t("sales.emails.copyOne"), props.t)}
          </button>
          {props.open && email.subject && (
            <button type="button" className="secondary-button compact-button" onClick={props.onCopySubject}>
              {copyLabel(props.subjectCopyState, props.t("sales.emails.copySubject"), props.t)}
            </button>
          )}
          <button type="button" className="quiet-link ec-card-toggle" onClick={props.onOpen} aria-expanded={props.open}>
            {props.open ? props.t("sales.emails.card.close") : props.t("sales.emails.card.open")}
          </button>
          <small className="ec-card-position">
            {replace(props.t("sales.emails.card.position"), { index: email.index, total: props.total })}
          </small>
        </div>
      </div>
    </article>
  );
}

export function PhaseBand(props: {
  phase: CampaignPhase;
  fallbackTitle: string;
  countLabel: string;
  children: React.ReactNode;
}) {
  return (
    <section className="ec-phase">
      <header className="ec-phase-head">
        {/* h3.doc-section is what the reader scans for its chapter nav, so the
            phases become the document's navigation for free. */}
        <h3 className="doc-section ec-phase-title">
          {/* The numbers of the e-mails INSIDE the phase ("01-07"), so the
              band says which part of the campaign it is. */}
          <span className="doc-section-num" aria-hidden="true">{phaseOrderRange(props.phase)}</span>
          {props.phase.title ?? props.fallbackTitle}
        </h3>
        <p className="ec-phase-count">{props.countLabel}</p>
      </header>
      {props.phase.summary && <p className="ec-phase-summary">{props.phase.summary}</p>}
      <div className="ec-phase-list">{props.children}</div>
    </section>
  );
}

export function CampaignHeader(props: {
  campaign: EmailCampaign;
  span: { from: string; to: string } | null;
  t: (key: string) => string;
  onCopyAll: () => void;
  copyAllState: "idle" | "done" | "failed";
}) {
  const phaseCount = props.campaign.phases.filter((phase) => phase.title).length;
  return (
    <header className="ec-head">
      {props.campaign.lead && <p className="ec-lead">{props.campaign.lead}</p>}
      <div className="ec-head-row">
        <ul className="ec-facts">
          <li>
            <strong>{props.campaign.emails.length}</strong>
            <span>{props.t("sales.emails.facts.emails")}</span>
          </li>
          {phaseCount > 0 && (
            <li>
              <strong>{phaseCount}</strong>
              <span>{props.t("sales.emails.facts.phases")}</span>
            </li>
          )}
          {props.span && (
            <li className="ec-facts-span">
              <strong>{props.span.from} {"→"} {props.span.to}</strong>
              <span>{props.t("sales.emails.facts.span")}</span>
            </li>
          )}
        </ul>
        <button type="button" className="secondary-button compact-button ec-copy-all" onClick={props.onCopyAll}>
          {props.copyAllState === "done"
            ? props.t("sales.emails.copied")
            : props.copyAllState === "failed"
              ? props.t("sales.emails.copyFailedShort")
              : props.t("sales.emails.copyAll")}
        </button>
      </div>
      <p className="ec-head-hint">{props.t("sales.emails.headHint")}</p>
    </header>
  );
}
